import React, { useState, useEffect } from 'react'
import {
  Button,
  Input,
  Label,
  Avatar,
  PasswordInput,
  RoleBadge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  type SystemRole,
} from '@agion/ui'
import { getInitials } from '../utils/stringHelpers'
import { Lock, Save, Edit3, Building2, Globe } from 'lucide-react'
import { authService } from '../services/auth'
import { config } from '../utils/config'
import { readRefusal } from '../services/api-client'
import { useTranslation } from 'react-i18next'

// F24: language preference lives here, mirroring admin-ui ProfilePage.
// Backend contract (PATCH /v1/users/{id}): `preferred_language` MUST be one
// of the supported languages — auth-service rejects anything else with 400.
// See auth-service/internal/handlers/api/user_handler_profile.go.
import {
  SUPPORTED_LANGUAGES,
  isSupportedLanguage,
  languageDisplayName,
  type SupportedLanguage,
} from '../app/i18n-resources'

interface UserProfile {
  id: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  organization_id: string
  organizationName?: string
  role?: string
  authProvider?: 'google' | 'microsoft' | 'email'
}

// Helper function to get friendly provider name - requires t function for i18n
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getProviderDisplayName = (provider: string | undefined, t: (key: string, options?: any) => string): string => {
  switch (provider) {
    case 'google':
      return t('pages:profile.providers.google', { defaultValue: 'Google' })
    case 'microsoft':
      return t('pages:profile.providers.microsoft', { defaultValue: 'Microsoft' })
    case 'email':
      return t('pages:profile.providers.email', { defaultValue: 'Email & Password' })
    default:
      return t('pages:profile.providers.agion', { defaultValue: 'Agion' })
  }
}

export const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation(['pages', 'common'])
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Load user profile from session
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await authService.getUserProfile()
        if (profile) {
          // Parse name into first/last if not already split
          const nameParts = profile.name?.split(' ') || []
          const profileWithName = profile as UserProfile & { firstName?: string; lastName?: string; role?: string; authProvider?: string }
          const extendedProfile: UserProfile = {
            ...profile,
            firstName: profileWithName.firstName || nameParts[0] || '',
            lastName: profileWithName.lastName || nameParts.slice(1).join(' ') || '',
            role: profileWithName.role || 'user',
            authProvider: profileWithName.authProvider as UserProfile['authProvider'],
          }
          setUserProfile(extendedProfile)
          setFirstName(extendedProfile.firstName || '')
          setLastName(extendedProfile.lastName || '')
        }
      } catch (error) {
        console.error('Failed to load user profile:', error)
      } finally {
        setIsLoadingProfile(false)
      }
    }
    loadProfile()
  }, [])

  // Language preference state. i18n already reflects localStorage on bootstrap
  // (i18next LanguageDetector); on save we update i18n + localStorage + the
  // server profile so the choice follows the user across browsers/devices.
  const initialLanguage: SupportedLanguage = isSupportedLanguage(i18n.language)
    ? (i18n.language as SupportedLanguage)
    : 'en'
  const [preferredLanguage, setPreferredLanguage] = useState<SupportedLanguage>(initialLanguage)
  const [isSavingLanguage, setIsSavingLanguage] = useState(false)
  const [languageError, setLanguageError] = useState('')

  const handleLanguageChange = async (next: string) => {
    if (!isSupportedLanguage(next) || !userProfile?.id) return
    if (next === preferredLanguage) return

    setLanguageError('')
    setIsSavingLanguage(true)
    try {
      const response = await fetch(`${config.apiUrl}/v1/users/${userProfile.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferred_language: next }),
      })
      if (!response.ok) {
        throw new Error((await readRefusal(response)) ?? 'Failed to save language preference')
      }
      // Apply immediately; server persistence handles cross-device consistency.
      await i18n.changeLanguage(next)
      localStorage.setItem('i18nextLng', next)
      setPreferredLanguage(next)
    } catch (error: any) {
      console.error('Failed to save language preference:', error)
      setLanguageError(
        error?.message ||
          t('pages:profile.preferences.languageSaveFailed', {
            defaultValue: 'Failed to save language preference. Please try again.',
          }),
      )
    } finally {
      setIsSavingLanguage(false)
    }
  }

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return t('pages:profile.validation.minLength', 'Password must be at least 8 characters long')
    }
    if (!/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password)) {
      return t('pages:profile.validation.complexity', 'Password must contain at least one number or special character')
    }
    return null
  }

  const handleChangePassword = async () => {
    if (!userProfile?.id) return

    setPasswordError('')
    setPasswordSuccess(false)

    if (newPassword !== confirmPassword) {
      setPasswordError(t('pages:profile.passwordMismatch'))
      return
    }

    const validationError = validatePassword(newPassword)
    if (validationError) {
      setPasswordError(validationError)
      return
    }

    setIsChangingPassword(true)
    try {
      // Use user ID in endpoint path like admin-ui
      const response = await fetch(`${config.apiUrl}/v1/users/${userProfile.id}/password`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      })

      if (!response.ok) {
        throw new Error((await readRefusal(response)) ?? 'Failed to change password')
      }

      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')

      setTimeout(() => setPasswordSuccess(false), 3000)
    } catch (error: any) {
      console.error('Failed to change password:', error)
      setPasswordError(error.message || t('pages:profile.passwordError'))
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleSaveName = async () => {
    if (!userProfile?.id) return

    setSaveError('')
    setSaveSuccess(false)

    try {
      // Call backend API to update user profile
      const response = await fetch(`${config.apiUrl}/v1/users/${userProfile.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName
        })
      })

      if (!response.ok) {
        throw new Error((await readRefusal(response)) ?? 'Failed to update profile')
      }

      const data = await response.json()

      // Update local state with response
      const updatedFirstName = data.first_name || firstName
      const updatedLastName = data.last_name || lastName
      const updatedName = `${updatedFirstName} ${updatedLastName}`.trim()

      setUserProfile(prev => prev ? {
        ...prev,
        firstName: updatedFirstName,
        lastName: updatedLastName,
        name: updatedName
      } : null)

      setSaveSuccess(true)
      setIsEditing(false)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error: any) {
      console.error('Failed to save name changes:', error)
      setSaveError(error.message || t('pages:profile.saveError', 'Failed to save changes. Please try again.'))
    }
  }

  if (isLoadingProfile) {
    return (
      <div className="container-standard">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('pages:profile.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container-standard">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">{t('pages:profile.title')}</h1>
        <p className="page-description">{t('pages:profile.subtitle')}</p>
      </div>

      <div className="space-y-8">
        {/* Profile Information */}
        <div className="card-standard card-padding-md rounded-level-2">
          <div className="flex items-start gap-6 mb-6">
            <Avatar
              fallback={getInitials(userProfile?.name || userProfile?.email)}
              size="xl"
            />
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {userProfile?.name || userProfile?.email || t('common:user')}
              </h3>
              <div className="space-y-2">
                <RoleBadge
                  role={(userProfile?.role || 'user') as SystemRole}
                  emphasis="strong"
                  size="sm"
                />
                {/* The organization id stays hidden (agion/Agion#2717): without a name the line is left out. */}
                {userProfile?.organizationName?.trim() && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-testid="profile-organization">
                    <Building2 className="h-4 w-4" />
                    <span>{userProfile.organizationName}</span>
                  </div>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('pages:profile.connectedVia', { provider: getProviderDisplayName(userProfile?.authProvider, t) })}
                </p>
              </div>
            </div>
          </div>

          {/* Success/Error Messages */}
          {saveSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">
                {t('pages:profile.updateSuccess')}
              </p>
            </div>
          )}

          {saveError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">
                {saveError}
              </p>
            </div>
          )}

          {/* Editable First Name and Last Name */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('pages:profile.firstName')}
                </Label>
                {isEditing ? (
                  <Input
                    value={firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                    placeholder={t('pages:profile.firstNamePlaceholder')}
                  />
                ) : (
                  <Input value={firstName || t('pages:profile.notSet')} disabled />
                )}
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('pages:profile.lastName')}
                </Label>
                {isEditing ? (
                  <Input
                    value={lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                    placeholder={t('pages:profile.lastNamePlaceholder')}
                  />
                ) : (
                  <Input value={lastName || t('pages:profile.notSet')} disabled />
                )}
              </div>
            </div>

            {/* Edit/Save Buttons */}
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button onClick={handleSaveName} size="sm">
                    <Save className="h-4 w-4" />
                    {t('common:save')}
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditing(false)
                      setFirstName(userProfile?.firstName || '')
                      setLastName(userProfile?.lastName || '')
                      setSaveError('')
                    }}
                    variant="secondary"
                    size="sm"
                  >
                    {t('common:cancel')}
                  </Button>
                </>
              ) : (
                <Button onClick={() => setIsEditing(true)} variant="secondary" size="sm" className="gap-1">
                  <Edit3 className="h-4 w-4" />
                  {t('pages:profile.edit', 'Edit')}
                </Button>
              )}
            </div>

            {/* Read-only Email */}
            <div>
              <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('pages:profile.email')}
              </Label>
              <Input value={userProfile?.email || ''} disabled />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('pages:profile.emailCannotChange')}
              </p>
            </div>
          </div>
        </div>

        {/* Preferences — language (F24: moved here from sidebar to match admin-ui) */}
        <div className="card-standard card-padding-md rounded-level-2">
          <div className="flex items-center gap-2 mb-6">
            <Globe className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('pages:profile.preferences.title', { defaultValue: 'Preferences' })}
            </h2>
          </div>

          <div className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="preferred-language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('pages:profile.preferences.languageLabel', { defaultValue: 'Language' })}
              </Label>
              <Select
                value={preferredLanguage}
                onValueChange={handleLanguageChange}
                disabled={isSavingLanguage}
              >
                <SelectTrigger
                  id="preferred-language"
                  aria-label={t('pages:profile.preferences.languageLabel', { defaultValue: 'Language' })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {languageDisplayName(code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('pages:profile.preferences.languageHelp', {
                  defaultValue: 'Saved to your account — your language follows you across browsers.',
                })}
              </p>
              {languageError && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">{languageError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div className="card-standard card-padding-md rounded-level-2">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('pages:profile.changePassword')}
            </h2>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleChangePassword()
            }}
            className="space-y-4 max-w-md"
          >
            {passwordSuccess && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  {t('pages:profile.passwordSuccess')}
                </p>
              </div>
            )}

            {passwordError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">
                  {passwordError}
                </p>
              </div>
            )}

            <div>
              <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('pages:profile.currentPassword')}
              </Label>
              <PasswordInput
                id="current-password"
                value={currentPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentPassword(e.target.value)}
                placeholder={t('pages:profile.currentPasswordPlaceholder')}
                disabled={isChangingPassword}
                autoComplete="current-password"
              />
            </div>

            <div>
              <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('pages:profile.newPassword')}
              </Label>
              <PasswordInput
                id="new-password"
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                placeholder={t('pages:profile.newPasswordPlaceholder')}
                disabled={isChangingPassword}
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('pages:profile.newPasswordHint')}
              </p>
            </div>

            <div>
              <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('pages:profile.confirmPassword')}
              </Label>
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                placeholder={t('pages:profile.confirmPasswordPlaceholder')}
                disabled={isChangingPassword}
                autoComplete="new-password"
              />
            </div>

            <Button
              type="submit"
              disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
            >
              <Lock className="h-4 w-4 mr-2" />
              {isChangingPassword ? t('pages:profile.changingPassword') : t('pages:profile.changePassword')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
